import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, X } from 'lucide-react';
import { Button } from '@/core/components';

type ThemeMode = 'light' | 'dark';
export type AppLanguage = 'en' | 'ro';

interface ConfirmOptions {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'default' | 'danger';
}

interface ProductUXContextValue {
  themeMode: ThemeMode;
  isDarkMode: boolean;
  language: AppLanguage;
  setLanguage: (language: AppLanguage) => void;
  t: (key: string) => string;
  toggleThemeMode: () => void;
  confirmAction: (options: ConfirmOptions) => Promise<boolean>;
}

const ProductUXContext = createContext<ProductUXContextValue | null>(null);

const THEME_STORAGE_KEY = 'fuchsia-theme-mode';
const LANGUAGE_STORAGE_KEY = 'fuchsia-app-language';

const translations: Record<string, Record<AppLanguage, string>> = {
  'nav.Dashboard': { en: 'Dashboard', ro: 'Panou principal' },
  'nav.Clients': { en: 'Clients', ro: 'Clienți' },
  'nav.Caregivers': { en: 'Caregivers', ro: 'Careri' },
  'nav.Recruitment & HR': { en: 'Recruitment & HR', ro: 'Recrutare și HR' },
  'nav.Branches': { en: 'Branches', ro: 'Branch-uri' },
  'nav.Scheduling': { en: 'Scheduling', ro: 'Programări' },
  'nav.Calendar View': { en: 'Calendar View', ro: 'Calendar' },
  'nav.Schedule Optimizer': { en: 'Schedule Optimizer', ro: 'Optimizare program' },
  'nav.Care Plans': { en: 'Care Plans', ro: 'Planuri de îngrijire' },
  'nav.Create Care Plan': { en: 'Create Care Plan', ro: 'Creează plan' },
  'nav.Tasks': { en: 'Tasks', ro: 'Task-uri' },
  'nav.Time Tracking': { en: 'Time Tracking', ro: 'Pontaj' },
  'nav.Billing': { en: 'Billing', ro: 'Facturare' },
  'nav.Carer Pay': { en: 'Carer Pay', ro: 'Salarii careri' },
  'nav.Fleet Costs': { en: 'Fleet Costs', ro: 'Costuri flotă' },
  'nav.Live Carers': { en: 'Live Carers', ro: 'Careri live' },
  'nav.Admin AI': { en: 'Admin AI', ro: 'AI Admin' },
  'nav.Assistant AI': { en: 'Assistant AI', ro: 'Assistant AI' },
  'nav.Reports': { en: 'Reports', ro: 'Rapoarte' },
  'nav.Automations': { en: 'Automations', ro: 'Automatizări' },
  'nav.Admin Guard': { en: 'Admin Guard', ro: 'Control erori' },
  'nav.Money Savings': { en: 'Money Savings', ro: 'Economii' },
  'nav.Documents': { en: 'Documents', ro: 'Documente' },
  'nav.Production Legal': { en: 'Production Legal', ro: 'Legal producție' },
  'nav.API Keys': { en: 'API Keys', ro: 'Chei API' },
  'nav.Incidents': { en: 'Incidents', ro: 'Incidente' },
  'nav.Quality Assurance': { en: 'Quality Assurance', ro: 'Calitate' },
  'nav.Compliance': { en: 'Compliance', ro: 'Conformitate' },
  'nav.Admin': { en: 'Admin', ro: 'Administrare' },
  'nav.Owner Monitor': { en: 'Owner Monitor', ro: 'Monitor owner' },
  'nav.User Credentials': { en: 'User Credentials', ro: 'Credentiale utilizatori' },
  'nav.Company Setup': { en: 'Company Setup', ro: 'Configurare firmă' },
  'nav.Assistant AI Control': { en: 'Assistant AI Control', ro: 'Control Assistant AI' },
  'nav.Readiness': { en: 'Readiness', ro: 'Pregătire' },
  'nav.Workflows': { en: 'Workflows', ro: 'Fluxuri' },
  'nav.GP Connect': { en: 'GP Connect', ro: 'GP Connect' },
  'nav.Security': { en: 'Security', ro: 'Securitate' },
  'nav.Roles & Access': { en: 'Roles & Access', ro: 'Roluri și acces' },
  'nav.Settings': { en: 'Settings', ro: 'Setări' },
  'nav.Logout': { en: 'Logout', ro: 'Deconectare' },
  'section.Core': { en: 'Core', ro: 'Principal' },
  'section.Operations': { en: 'Operations', ro: 'Operațiuni' },
  'section.Care': { en: 'Care', ro: 'Îngrijire' },
  'section.Finance': { en: 'Finance', ro: 'Financiar' },
  'section.Intelligence': { en: 'Intelligence', ro: 'Inteligență' },
  'section.Governance': { en: 'Governance', ro: 'Guvernanță' },
  'section.SuperAdmin': { en: 'SuperAdmin', ro: 'SuperAdmin' },
  'section.System': { en: 'System', ro: 'Sistem' },
  'header.product': { en: 'Professional care management system', ro: 'Sistem profesional de management al îngrijirii' },
  'settings.title': { en: 'Settings', ro: 'Setări' },
  'settings.subtitle': { en: 'Manage your account settings and preferences', ro: 'Administrează contul și preferințele tale' },
  'settings.profile': { en: 'Profile', ro: 'Profil' },
  'settings.account': { en: 'Account', ro: 'Cont' },
  'settings.preferences': { en: 'Preferences', ro: 'Preferințe' },
  'settings.preferencesSubtitle': { en: 'Customize your experience', ro: 'Personalizează experiența ta' },
  'settings.team': { en: 'Team', ro: 'Echipă' },
  'settings.languageTitle': { en: 'Application language', ro: 'Limba aplicației' },
  'settings.languageHelp': { en: 'English is the native language of the application. Non-family users can switch the interface and AI assistant to Romanian.', ro: 'Engleza este limba nativă a aplicației. Utilizatorii non-Family pot schimba interfața și Assistantul AI în română.' },
  'settings.language': { en: 'Language', ro: 'Limbă' },
  'settings.english': { en: 'English', ro: 'Engleză' },
  'settings.romanian': { en: 'Romanian', ro: 'Română' },
  'settings.languageSaved': { en: 'Language preference saved', ro: 'Preferința de limbă a fost salvată' },
  'settings.profileInfo': { en: 'Profile Information', ro: 'Informații profil' },
  'settings.profileSubtitle': { en: 'Update your personal information', ro: 'Actualizează informațiile personale' },
  'settings.firstName': { en: 'First Name', ro: 'Prenume' },
  'settings.lastName': { en: 'Last Name', ro: 'Nume' },
  'settings.email': { en: 'Email Address', ro: 'Adresă email' },
  'settings.saveChanges': { en: 'Save Changes', ro: 'Salvează modificările' },
  'settings.cancel': { en: 'Cancel', ro: 'Anulează' },
  'settings.changePassword': { en: 'Change Password', ro: 'Schimbă parola' },
  'settings.changePasswordSubtitle': { en: 'Update your password to keep your account secure', ro: 'Actualizează parola pentru securitatea contului' },
  'settings.currentPassword': { en: 'Current Password', ro: 'Parola actuală' },
  'settings.newPassword': { en: 'New Password', ro: 'Parolă nouă' },
  'settings.confirmPassword': { en: 'Confirm New Password', ro: 'Confirmă parola nouă' },
  'settings.notifications': { en: 'Notifications', ro: 'Notificări' },
  'settings.emailNotifications': { en: 'Email notifications', ro: 'Notificări email' },
  'settings.pushNotifications': { en: 'Push notifications', ro: 'Notificări push' },
  'settings.appearance': { en: 'Appearance', ro: 'Aspect' },
  'settings.theme': { en: 'Theme', ro: 'Temă' },
  'settings.light': { en: 'Light', ro: 'Luminoasă' },
  'settings.dark': { en: 'Dark', ro: 'Întunecată' },
  'settings.system': { en: 'System', ro: 'Sistem' },
  'settings.savePreferences': { en: 'Save Preferences', ro: 'Salvează preferințele' },
};

const pageTextTranslations: Record<string, string> = {
  'Dashboard': 'Panou principal',
  'Admin Dashboard': 'Panou administrator',
  'Owner Dashboard': 'Panou proprietar',
  'Administrator Dashboard': 'Panou administrator',
  'Administrator Actions': 'Acțiuni administrator',
  'Company Contact': 'Contact firmă',
  'Clients': 'Clienți',
  'Caregivers': 'Careri',
  'Caregivers Management': 'Management careri',
  'Scheduling': 'Programări',
  'Calendar View': 'Calendar',
  'Schedule Optimizer': 'Optimizare program',
  'Care Plans': 'Planuri de îngrijire',
  'Create Care Plan': 'Creează plan de îngrijire',
  'Tasks': 'Task-uri',
  'Time Tracking': 'Pontaj',
  'Billing': 'Facturare',
  'Carer Pay': 'Salarii careri',
  'Live Carers': 'Careri live',
  'Reports': 'Rapoarte',
  'Documents': 'Documente',
  'Incidents': 'Incidente',
  'Compliance': 'Conformitate',
  'Quality Assurance': 'Calitate',
  'Security': 'Securitate',
  'Settings': 'Setări',
  'Branches & Locations': 'Branch-uri și locații',
  'Branch details': 'Detalii branch',
  'Branch report': 'Raport pe branch',
  'Multi-branch control': 'Control multi-branch',
  'Clients by branch': 'Clienți pe branch',
  'Carers by branch': 'Careri pe branch',
  'Separate permissions per branch': 'Permisiuni separate pe branch',
  'Open Roles & Access': 'Deschide roluri și acces',
  'Roles & Access': 'Roluri și acces',
  'API Keys and Integrations': 'Chei API și integrări',
  'API Key Activation Status': 'Status activare chei API',
  'API key ON/OFF controls': 'Controale PORNIT/OPRIT pentru chei API',
  'API key activation status': 'Status activare chei API',
  'Activation Controls': 'Controale activare',
  'Configured Keys': 'Chei configurate',
  'Missing Keys': 'Chei lipsă',
  'Disabled Keys': 'Chei dezactivate',
  'Administrator Configuration': 'Configurare administrator',
  'Administrator Read-Only View': 'Vizualizare administrator doar citire',
  'Server admin token': 'Token administrator server',
  'Server token required': 'Tokenul serverului este necesar',
  'Server connected': 'Server conectat',
  'Server auth failed': 'Autentificarea serverului a eșuat',
  'Auto-save is active': 'Salvarea automată este activă',
  'Last local save:': 'Ultima salvare locală:',
  'Load from server': 'Încarcă de pe server',
  'Save to server': 'Salvează pe server',
  'Add API': 'Adaugă API',
  'Add key field': 'Adaugă câmp cheie',
  'Verify & save': 'Verifică și salvează',
  'Open candidates': 'Candidați deschiși',
  'Active candidates': 'Candidați activi',
  'Ready to start': 'Gata de început',
  'Blocked checks': 'Verificări blocate',
  'Missing documents': 'Documente lipsă',
  'HR compliance dashboard': 'Panou conformitate HR',
  'Candidate pipeline': 'Flux candidați',
  'Candidates': 'Candidați',
  'Active': 'Activ',
  'Inactive': 'Inactiv',
  'configured': 'configurat',
  'missing': 'lipsește',
  'disabled': 'dezactivat',
  'integration active': 'integrare activă',
  'integration inactive': 'integrare inactivă',
  'ON': 'PORNIT',
  'OFF': 'OPRIT',
  'On': 'Pornit',
  'Off': 'Oprit',
  'Integration': 'Integrare',
  'Category': 'Categorie',
  'Required for': 'Necesar pentru',
  'Target': 'Țintă',
  'Purpose': 'Scop',
  'Production and legal': 'Producție și legal',
  'Legal Readiness Centre': 'Centru pregătire legală',
  'Document register': 'Registru documente',
  'Ready for review': 'Gata pentru verificare',
  'Needs company details': 'Necesită datele firmei',
  'Requires signed contract': 'Necesită contract semnat',
  'Operational policy': 'Politică operațională',
  'Required actions': 'Acțiuni necesare',
  'Required completion actions': 'Acțiuni necesare pentru finalizare',
  'Draft content': 'Conținut draft',
  'Official reference points': 'Referințe oficiale',
  'Owner': 'Responsabil',
  'Review cycle': 'Ciclu de revizuire',
  'Download PDF': 'Descarcă PDF',
  'Download Report PDF': 'Descarcă raport PDF',
  'Print PDF': 'Printează PDF',
  'Print summary': 'Printează rezumat',
  'Print': 'Printează',
  'Download': 'Descarcă',
  'Save': 'Salvează',
  'Save branch': 'Salvează branch',
  'New branch': 'Branch nou',
  'Refresh': 'Reîmprospătează',
  'Name': 'Nume',
  'Status': 'Status',
  'Address': 'Adresă',
  'Postcode': 'Cod poștal',
  'Region': 'Regiune',
  'Phone': 'Telefon',
  'On-call': 'De gardă',
  'Email': 'Email',
  'Manager': 'Manager',
  'Branch': 'Branch',
  'Contact': 'Contact',
  'Unassigned': 'Nealocat',
  'Branch profit': 'Profit branch',
  'Carers': 'Careri',
  'Visits': 'Vizite',
  'Completion': 'Finalizare',
  'Revenue': 'Venituri',
  'Payroll': 'Salarii',
  'Profit': 'Profit',
  'Day': 'Zi',
  'Week': 'Săptămână',
  'Month': 'Lună',
  'Quarter': 'Trimestru',
  'AI Assistant and Time-Saving Control Center': 'Asistent AI și centru pentru economie de timp',
  'AI Assistant for quick questions': 'Asistent AI pentru întrebări rapide',
  'Natural command bar': 'Bară de comenzi naturale',
  'Automatic morning briefing': 'Briefing automat de dimineață',
  'End-of-day report': 'Raport automat de final de zi',
  'Automatic urgent alerts': 'Alerte urgente automate',
  'Automatic weekly and monthly reports': 'Rapoarte automate săptămânale și lunare',
  'Smart search across the application': 'Căutare inteligentă în aplicație',
  'Role-personalised admin dashboard': 'Panou administrator personalizat pe rol',
  'Operational snapshot': 'Rezumat operațional',
  'Human approval': 'Aprobare umană',
  'Required': 'Necesară',
  'Optional': 'Opțională',
  'Pending AI drafts': 'Drafturi AI în așteptare',
  'Last intent': 'Ultima intenție',
  'Real database context': 'Context bază de date reală',
  'Checking': 'Se verifică',
  'Open': 'Deschide',
  'Closed': 'Închis',
  'Completed': 'Finalizat',
  'Scheduled': 'Programat',
  'In Progress': 'În desfășurare',
  'Pending': 'În așteptare',
  'Approved': 'Aprobat',
  'Rejected': 'Respins',
  'Cancel': 'Anulează',
  'Confirm': 'Confirmă',
  'Search': 'Caută',
  'Filter': 'Filtru',
  'Export': 'Exportă',
  'Import': 'Importă',
  'Edit': 'Editează',
  'Delete': 'Șterge',
  'View': 'Vezi',
  'Add': 'Adaugă',
  'Create': 'Creează',
  'Update': 'Actualizează',
  'Submit': 'Trimite',
  'Close': 'Închide',
  'Back': 'Înapoi',
  'Next': 'Următorul',
  'Previous': 'Anterior',
  'Loading': 'Se încarcă',
  'Loading...': 'Se încarcă...',
  'No data': 'Nu există date',
  'No records found': 'Nu au fost găsite înregistrări',
  'Sign in': 'Autentificare',
  'Password': 'Parolă',
  'Security verification': 'Verificare securitate',
  'Security App': 'Aplicație securitate',
  'Email / SMS': 'Email / SMS',
  'Forgot password / secure reset': 'Am uitat parola / resetare securizată',
  'Owner access': 'Acces proprietar',
  'Professional care management system': 'Sistem profesional de management al îngrijirii',
  'Sign in to the home care management application': 'Conectează-te în aplicația de management home care',
  'Need access?': 'Ai nevoie de acces?',
  'Contact the administrator': 'Contactează administratorul',
  'Open menu': 'Deschide meniul',
  'Notifications': 'Notificări',
  'Switch to light mode': 'Comută la modul luminos',
  'Switch to dark mode': 'Comută la modul întunecat',
  'Switch application to English': 'Schimbă aplicația în engleză',
  'Switch application to Romanian': 'Schimbă aplicația în română',
};

const reversePageTextTranslations: Record<string, string> = Object.fromEntries(
  Object.entries(pageTextTranslations).map(([english, romanian]) => [romanian, english])
);

const sortedRomanianEntries = Object.entries(pageTextTranslations).sort((left, right) => right[0].length - left[0].length);
const sortedEnglishEntries = Object.entries(reversePageTextTranslations).sort((left, right) => right[0].length - left[0].length);
const canonicalTextTerms = Array.from(new Set([...Object.keys(pageTextTranslations), ...Object.values(pageTextTranslations)]))
  .filter((term) => term.length >= 4 && /[A-Za-zăâîșțĂÂÎȘȚ]$/.test(term))
  .sort((left, right) => right.length - left.length);
const textNodeOriginals = new WeakMap<Text, string>();
const attributeOriginals = new WeakMap<Element, Map<string, string>>();

function translate(language: AppLanguage, key: string) {
  return translations[key]?.[language] ?? translations[key]?.en ?? key;
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function replaceTermSafe(value: string, search: string, replacement: string) {
  const pattern = new RegExp(`(^|[^\\p{L}\\p{N}_])${escapeRegExp(search)}(?=$|[^\\p{L}\\p{N}_])`, 'gu');
  return value.replace(pattern, (_match, prefix: string) => `${prefix}${replacement}`);
}

function cleanTextArtifacts(value: string) {
  let next = value.replace(/([A-Za-z])\1{3,}/g, '$1');
  for (const term of canonicalTextTerms) {
    const lastChar = term[term.length - 1];
    next = next.replace(new RegExp(`\\b${escapeRegExp(term)}${escapeRegExp(lastChar)}+\\b`, 'g'), term);
  }
  return next;
}

function translateVisibleString(value: string, language: AppLanguage) {
  const cleanValue = cleanTextArtifacts(value);
  if (!cleanValue.trim()) return cleanValue;
  const exactDictionary = language === 'ro' ? pageTextTranslations : reversePageTextTranslations;
  const trimmed = cleanValue.trim();
  const exact = exactDictionary[trimmed];
  if (exact) {
    return cleanValue.replace(trimmed, exact);
  }

  if (language === 'en') {
    let next = cleanValue;
    for (const [source, target] of sortedEnglishEntries) {
      if (source.length < 4 || !next.includes(source)) continue;
      next = replaceTermSafe(next, source, target);
    }
    return next;
  }

  const entries = sortedRomanianEntries;
  let next = cleanValue;
  for (const [source, target] of entries) {
    if (source.length < 4 || !next.includes(source)) continue;
    next = replaceTermSafe(next, source, target);
  }
  return next;
}

function getStableTextOriginal(node: Text, language: AppLanguage) {
  const current = cleanTextArtifacts(node.nodeValue || '');
  const stored = textNodeOriginals.get(node);
  if (!stored) {
    textNodeOriginals.set(node, current);
    return current;
  }

  const translatedFromStored = translateVisibleString(stored, language);
  const englishFromStored = translateVisibleString(stored, 'en');
  const romanianFromStored = translateVisibleString(stored, 'ro');
  if (current !== translatedFromStored && current !== englishFromStored && current !== romanianFromStored) {
    textNodeOriginals.set(node, current);
    return current;
  }

  return stored;
}

function getStableAttributeOriginal(element: Element, attribute: string, currentValue: string, language: AppLanguage) {
  const current = cleanTextArtifacts(currentValue);
  const originals = attributeOriginals.get(element) || new Map<string, string>();
  const stored = originals.get(attribute);
  if (!stored) {
    originals.set(attribute, current);
    attributeOriginals.set(element, originals);
    return current;
  }

  const translatedFromStored = translateVisibleString(stored, language);
  const englishFromStored = translateVisibleString(stored, 'en');
  const romanianFromStored = translateVisibleString(stored, 'ro');
  if (current !== translatedFromStored && current !== englishFromStored && current !== romanianFromStored) {
    originals.set(attribute, current);
    return current;
  }

  return stored;
}

function translateVisiblePage(language: AppLanguage) {
  if (typeof document === 'undefined' || !document.body) return;
  const ignoredTags = new Set(['SCRIPT', 'STYLE', 'TEXTAREA', 'CODE', 'PRE', 'NOSCRIPT']);
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentElement;
      if (!parent || ignoredTags.has(parent.tagName) || parent.closest('[data-no-auto-translate="true"]')) {
        return NodeFilter.FILTER_REJECT;
      }
      return node.nodeValue?.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
    },
  });

  const nodes: Text[] = [];
  while (walker.nextNode()) nodes.push(walker.currentNode as Text);
  nodes.forEach((node) => {
    const original = getStableTextOriginal(node, language);
    const translated = translateVisibleString(original, language);
    if (translated !== node.nodeValue) node.nodeValue = translated;
  });

  const attributes = ['placeholder', 'title', 'aria-label', 'value'];
  document.querySelectorAll<HTMLElement>('[placeholder],[title],[aria-label],input[type="button"],input[type="submit"]').forEach((element) => {
    if (element.closest('[data-no-auto-translate="true"]')) return;
    attributes.forEach((attribute) => {
      const current = element.getAttribute(attribute);
      if (!current) return;
      const original = getStableAttributeOriginal(element, attribute, current, language);
      const translated = translateVisibleString(original, language);
      if (translated !== current) element.setAttribute(attribute, translated);
    });
  });
}

export const ProductUXProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [themeMode, setThemeMode] = useState<ThemeMode>(() => {
    if (typeof window === 'undefined') return 'light';
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });
  const [language, setLanguageState] = useState<AppLanguage>(() => {
    if (typeof window === 'undefined') return 'en';
    return window.localStorage.getItem(LANGUAGE_STORAGE_KEY) === 'ro' ? 'ro' : 'en';
  });
  const [isRouteLoading, setIsRouteLoading] = useState(false);
  const [confirmRequest, setConfirmRequest] = useState<{
    options: ConfirmOptions;
    resolve: (confirmed: boolean) => void;
  } | null>(null);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', themeMode === 'dark');
    root.dataset.theme = themeMode;
    root.lang = language;
    root.dataset.language = language;
    window.localStorage.setItem(THEME_STORAGE_KEY, themeMode);
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  }, [themeMode, language]);

  useEffect(() => {
    setIsRouteLoading(true);
    const timer = window.setTimeout(() => setIsRouteLoading(false), 420);
    return () => window.clearTimeout(timer);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const runTranslation = () => translateVisiblePage(language);
    const timers = [
      window.setTimeout(runTranslation, 0),
      window.setTimeout(runTranslation, 150),
      window.setTimeout(runTranslation, 600),
    ];
    const observer = new MutationObserver(() => {
      window.requestAnimationFrame(runTranslation);
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['placeholder', 'title', 'aria-label', 'value'],
    });

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      observer.disconnect();
    };
  }, [language, location.pathname, location.search]);

  const toggleThemeMode = useCallback(() => {
    setThemeMode((current) => (current === 'dark' ? 'light' : 'dark'));
  }, []);

  const setLanguage = useCallback((nextLanguage: AppLanguage) => {
    setLanguageState(nextLanguage);
  }, []);

  const t = useCallback((key: string) => translate(language, key), [language]);

  const confirmAction = useCallback((options: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setConfirmRequest({ options, resolve });
    });
  }, []);

  const closeConfirm = useCallback((confirmed: boolean) => {
    setConfirmRequest((request) => {
      request?.resolve(confirmed);
      return null;
    });
  }, []);

  const value = useMemo<ProductUXContextValue>(
    () => ({
      themeMode,
      isDarkMode: themeMode === 'dark',
      language,
      setLanguage,
      t,
      toggleThemeMode,
      confirmAction,
    }),
    [confirmAction, language, setLanguage, t, themeMode, toggleThemeMode]
  );

  return (
    <ProductUXContext.Provider value={value}>
      {isRouteLoading && (
        <div className="fixed left-0 right-0 top-0 z-[70] h-1 overflow-hidden bg-purple-100 dark:bg-slate-800">
          <div className="h-full w-1/2 animate-[product-progress_0.9s_ease-in-out_infinite] bg-purple-700 dark:bg-fuchsia-300" />
        </div>
      )}
      {children}
      {confirmRequest && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/45 px-4 backdrop-blur-sm">
          <div
            role="dialog"
            aria-modal="true"
            className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-5 shadow-2xl dark:border-slate-700 dark:bg-slate-900"
          >
            <div className="flex items-start gap-3">
              <div
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                  confirmRequest.options.tone === 'danger'
                    ? 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300'
                    : 'bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300'
                }`}
              >
                {confirmRequest.options.tone === 'danger' ? (
                  <AlertTriangle className="h-5 w-5" />
                ) : (
                  <CheckCircle2 className="h-5 w-5" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-lg font-semibold text-slate-950 dark:text-white">
                  {confirmRequest.options.title}
                </h2>
                {confirmRequest.options.description && (
                  <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
                    {confirmRequest.options.description}
                  </p>
                )}
              </div>
              <button
                type="button"
                aria-label="Close confirmation"
                onClick={() => closeConfirm(false)}
                className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Button variant="outline" onClick={() => closeConfirm(false)} className="justify-center">
                {confirmRequest.options.cancelLabel ?? 'Cancel'}
              </Button>
              <Button
                variant={confirmRequest.options.tone === 'danger' ? 'danger' : 'primary'}
                onClick={() => closeConfirm(true)}
                className="justify-center"
              >
                {confirmRequest.options.confirmLabel ?? 'Confirm'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </ProductUXContext.Provider>
  );
};

export function useProductUX() {
  const context = useContext(ProductUXContext);
  if (!context) {
    throw new Error('useProductUX must be used inside ProductUXProvider');
  }
  return context;
}

export function useConfirmAction() {
  return useProductUX().confirmAction;
}
